import React, { useEffect, useState, useRef } from 'react';
import { SaveIcon, ArrowUpIcon, ArrowDownIcon } from 'lucide-react';
import { useSiteSettings } from '../../lib/cmsStore';
import { SiteSettings } from '../../lib/cmsTypes';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { VersionHistoryPanel, VersionHistoryButton } from './components/VersionHistoryPanel';
const NAV_SECTIONS = [{
  key: 'work',
  label: 'Work / Projects'
}, {
  key: 'about',
  label: 'About'
}, {
  key: 'music',
  label: 'Music'
}, {
  key: 'experiments',
  label: 'Experiments'
}, {
  key: 'writing',
  label: 'Writing'
}, {
  key: 'contact',
  label: 'Contact'
}];
export function Settings() {
  const {
    data,
    updateData,
    isLoading
  } = useSiteSettings();
  const [localData, setLocalData] = useState<SiteSettings | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  useEffect(() => {
    if (!isLoading && data && !localData) {
      setLocalData(data);
    }
  }, [data, isLoading, localData]);
  if (isLoading || !localData) {
    return <div className="p-8 font-mono text-sm text-muted-foreground" data-id="element-1519">
        Loading...
      </div>;
  }
  const handleSave = () => {
    setIsSaving(true);
    updateData(localData);
    setTimeout(() => setIsSaving(false), 500);
  };
  const updateField = (field: keyof SiteSettings, value: any) => {
    setLocalData(prev => prev ? {
      ...prev,
      [field]: value
    } : prev);
  };
  // Build ordered section list, falling back to default order
  const defaultOrder = NAV_SECTIONS.map(s => s.key);
  const currentOrder: string[] = localData.sectionOrder?.length ? localData.sectionOrder : defaultOrder;
  // Ensure all nav sections are present
  const orderedSections = [...currentOrder.filter(k => NAV_SECTIONS.some(s => s.key === k)), ...NAV_SECTIONS.filter(s => !currentOrder.includes(s.key)).map(s => s.key)];
  const moveSection = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0 || direction === 'down' && index === orderedSections.length - 1) return;
    const newOrder = [...orderedSections];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    const temp = newOrder[index];
    newOrder[index] = newOrder[targetIndex];
    newOrder[targetIndex] = temp;
    updateField('sectionOrder', newOrder);
  };
  const toggleSection = (key: string) => {
    const isOn = localData.sectionVisibility?.[key] !== false;
    setLocalData(prev => prev ? {
      ...prev,
      sectionVisibility: {
        ...prev.sectionVisibility,
        [key]: !isOn
      }
    } : prev);
  };
  return <div className="p-8 max-w-4xl mx-auto" data-id="element-1520">
      <div className="flex items-center justify-between mb-8" data-id="element-1521">
        <div data-id="element-1522">
          <h1 className="font-display text-3xl font-bold tracking-tight" data-id="element-1523">
            Global Settings
          </h1>
          <p className="font-mono text-sm text-muted-foreground mt-2" data-id="element-1524">
            Manage portfolio-wide configuration.
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-1525">
          <VersionHistoryButton onClick={() => setHistoryOpen(true)} data-id="element-1526" />
          <Button onClick={handleSave} disabled={isSaving} className="gap-2" data-id="element-1527">
            <SaveIcon className="w-4 h-4" data-id="element-1528" />
            {isSaving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>

      <div className="space-y-12 pb-24" data-id="element-1529">
        <section className="space-y-6" data-id="element-1530">
          <div className="border-b border-border pb-2" data-id="element-1531">
            <h2 className="font-display text-2xl font-bold" data-id="element-1532">General</h2>
          </div>
          <Input label="Site Title" value={localData.siteTitle} onChange={e => updateField('siteTitle', e.target.value)} data-id="element-1533" />
          <Textarea label="Site Description" value={localData.siteDescription} onChange={e => updateField('siteDescription', e.target.value)} rows={3} data-id="element-1534" />
          <Input label="Contact Email" value={localData.contactEmail} onChange={e => updateField('contactEmail', e.target.value)} data-id="element-1535" />
          <Input label="Footer Text" value={localData.footerText} onChange={e => updateField('footerText', e.target.value)} data-id="element-1536" />
        </section>

        <section className="space-y-6" data-id="element-1537">
          <div className="border-b border-border pb-2" data-id="element-1538">
            <h2 className="font-display text-2xl font-bold" data-id="element-1539">
              Section Visibility & Order
            </h2>
            <p className="font-mono text-xs text-muted-foreground mt-1" data-id="element-1540">
              Toggle sections on or off and drag to reorder. Order affects
              navigation links.
            </p>
          </div>

          <div className="border border-border divide-y divide-border" data-id="element-1541">
            {orderedSections.map((key, index) => {
            const section = NAV_SECTIONS.find(s => s.key === key)!;
            const isOn = localData.sectionVisibility?.[key] !== false;
            return <div key={key} className={`flex items-center gap-4 p-4 transition-colors ${isOn ? 'bg-card' : 'bg-card opacity-50'}`} data-id="element-1542">
                  {/* Reorder arrows */}
                  <div className="flex flex-col gap-0.5 shrink-0" data-id="element-1543">
                    <button onClick={() => moveSection(index, 'up')} disabled={index === 0} className="text-muted-foreground hover:text-foreground disabled:opacity-20 disabled:cursor-not-allowed p-0.5" data-id="element-1544">
                      <ArrowUpIcon className="w-3.5 h-3.5" data-id="element-1545" />
                    </button>
                    <button onClick={() => moveSection(index, 'down')} disabled={index === orderedSections.length - 1} className="text-muted-foreground hover:text-foreground disabled:opacity-20 disabled:cursor-not-allowed p-0.5" data-id="element-1546">
                      <ArrowDownIcon className="w-3.5 h-3.5" data-id="element-1547" />
                    </button>
                  </div>

                  {/* Index badge */}
                  <span className="font-mono text-xs text-muted-foreground w-5 shrink-0" data-id="element-1548">
                    {index + 1}
                  </span>

                  {/* Label */}
                  <span className="font-mono text-xs uppercase tracking-widest text-foreground flex-1" data-id="element-1549">
                    {section.label}
                  </span>

                  {/* Toggle */}
                  <button onClick={() => toggleSection(key)} className="shrink-0" aria-label={`Toggle ${section.label}`} data-id="element-1550">
                    <span className={`inline-flex items-center w-10 h-6 rounded-full relative transition-colors duration-200 ${isOn ? 'bg-primary' : 'bg-secondary'}`} data-id="element-1551">
                      <span className={`w-4 h-4 rounded-full bg-background shadow-sm transition-transform duration-200 ${isOn ? 'translate-x-5' : 'translate-x-1'}`} data-id="element-1552" />
                    </span>
                  </button>
                </div>;
          })}
          </div>

          {/* Game toggle separately since it's not a nav item */}
          <div className="border border-border" data-id="element-1553">
            <div className={`flex items-center gap-4 p-4 transition-colors ${localData.sectionVisibility?.game !== false ? 'bg-card' : 'bg-card opacity-50'}`} data-id="element-1554">
              <div className="w-9 shrink-0" data-id="element-1555" />
              <span className="font-mono text-xs text-muted-foreground w-5 shrink-0" data-id="element-1556">
                —
              </span>
              <span className="font-mono text-xs uppercase tracking-widest text-foreground flex-1" data-id="element-1557">
                Game (inside Contact)
              </span>
              <button onClick={() => toggleSection('game')} className="shrink-0" data-id="element-1558">
                <span className={`inline-flex items-center w-10 h-6 rounded-full relative transition-colors duration-200 ${localData.sectionVisibility?.game !== false ? 'bg-primary' : 'bg-secondary'}`} data-id="element-1559">
                  <span className={`w-4 h-4 rounded-full bg-background shadow-sm transition-transform duration-200 ${localData.sectionVisibility?.game !== false ? 'translate-x-5' : 'translate-x-1'}`} data-id="element-1560" />
                </span>
              </button>
            </div>
          </div>
        </section>

        <section className="space-y-6" data-id="element-1561">
          <div className="border-b border-border pb-2" data-id="element-1562">
            <h2 className="font-display text-2xl font-bold" data-id="element-1563">Design Tokens</h2>
            <p className="font-mono text-xs text-muted-foreground mt-1" data-id="element-1564">
              Global CSS variables applied to the site.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1565">
            <div data-id="element-1566">
              <label className="font-mono text-xs text-muted-foreground uppercase tracking-widest mb-1.5 block" data-id="element-1567">
                Primary Color
              </label>
              <div className="flex gap-2" data-id="element-1568">
                <input type="color" value={localData.primaryColor.startsWith('hsl') ? '#ccff00' : localData.primaryColor} onChange={e => updateField('primaryColor', e.target.value)} className="w-10 h-10 p-1 bg-background border border-border cursor-pointer" data-id="element-1569" />
                <Input value={localData.primaryColor} onChange={e => updateField('primaryColor', e.target.value)} className="flex-1" data-id="element-1570" />
              </div>
            </div>
            <div data-id="element-1571">
              <label className="font-mono text-xs text-muted-foreground uppercase tracking-widest mb-1.5 block" data-id="element-1572">
                Background Color
              </label>
              <div className="flex gap-2" data-id="element-1573">
                <input type="color" value={localData.backgroundColor.startsWith('hsl') ? '#0f0f0f' : localData.backgroundColor} onChange={e => updateField('backgroundColor', e.target.value)} className="w-10 h-10 p-1 bg-background border border-border cursor-pointer" data-id="element-1574" />
                <Input value={localData.backgroundColor} onChange={e => updateField('backgroundColor', e.target.value)} className="flex-1" data-id="element-1575" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1576">
            <Input label="Display Font Family" value={localData.displayFont} onChange={e => updateField('displayFont', e.target.value)} data-id="element-1577" />
            <Input label="Mono Font Family" value={localData.monoFont} onChange={e => updateField('monoFont', e.target.value)} data-id="element-1578" />
          </div>
        </section>
      </div>
      <VersionHistoryPanel contentKey="sinka_settings" isOpen={historyOpen} onClose={() => setHistoryOpen(false)} onRestore={data => {
      setLocalData(data);
      updateData(data);
    }} data-id="element-1579" />
    </div>;
}